// Round 3 (AR2-M07): actual final-payload budgeting for feedback captures.
//
// The server enforces byte caps on the composed capture payload
// (FEEDBACK_MAX_SCREENSHOT_BYTES / FEEDBACK_MAX_SVG_BYTES). This module makes
// the CLIENT measure the exact outbound representation — the serialized
// annotated SVG (base64 image, XML escaping, structure, text, every annotation
// type) and the screenshot data URL — before any network mutation, and derives
// a deterministic simplified OUTBOUND copy of pen geometry without ever
// touching the editable source points.

import { buildAnnotatedSvg } from './feedbackSvg.js';

// Conservative fallbacks matching the server defaults in
// apps/api/src/config/env.js — used only when GET /config/client is
// unavailable. The server remains authoritative.
export const FEEDBACK_PAYLOAD_FALLBACK_LIMITS = Object.freeze({
  maxScreenshotBytes: 8 * 1024 * 1024,
  maxSvgBytes: 2 * 1024 * 1024,
  // Matches feedbackAnnotation.ANNOTATION_MAX_POINTS, the hard ceiling of the
  // governed annotation snapshot and therefore the server default. A larger
  // fallback would let a stroke be saved that the snapshot cannot represent —
  // which the server now rejects at save time.
  maxPenPointsPerStroke: 2048,
  maxTotalAnnotationPoints: 20000,
});

// Documented simplification tolerance, in normalized (0..1) stroke-local
// units: points closer than this to the chord between their neighbors carry no
// visible information at any realistic zoom. Deterministic by construction
// (pure function of the input points).
export const PEN_SIMPLIFY_TOLERANCE = 0.002;

export const FEEDBACK_PAYLOAD_BLOCK_GUIDANCE =
  'This capture is too large to send. Remove or simplify some marks, capture a smaller region, or recapture — your marks and screenshot are still here and nothing was lost.';

function positiveInt(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
}

export function resolveFeedbackPayloadLimits(clientConfig) {
  const published = clientConfig?.feedback || {};
  return {
    maxScreenshotBytes:
      positiveInt(published.maxScreenshotBytes) ||
      FEEDBACK_PAYLOAD_FALLBACK_LIMITS.maxScreenshotBytes,
    maxSvgBytes: positiveInt(published.maxSvgBytes) || FEEDBACK_PAYLOAD_FALLBACK_LIMITS.maxSvgBytes,
    maxPenPointsPerStroke:
      positiveInt(published.maxPenPointsPerStroke) ||
      FEEDBACK_PAYLOAD_FALLBACK_LIMITS.maxPenPointsPerStroke,
    maxTotalAnnotationPoints:
      positiveInt(published.maxTotalAnnotationPoints) ||
      FEEDBACK_PAYLOAD_FALLBACK_LIMITS.maxTotalAnnotationPoints,
  };
}

const textEncoder = typeof TextEncoder === 'undefined' ? null : new TextEncoder();

export function utf8ByteLength(text) {
  const value = String(text ?? '');
  if (textEncoder) return textEncoder.encode(value).length;
  // Fallback: encodeURIComponent expands each UTF-8 byte to %XX or a literal.
  return encodeURIComponent(value).replace(/%[0-9A-F]{2}/gi, '.').length;
}

export function dataUrlByteLength(dataUrl) {
  const value = String(dataUrl || '');
  const commaIndex = value.indexOf(',');
  if (commaIndex === -1) return 0;
  const body = value.slice(commaIndex + 1);
  const padding = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((body.length * 3) / 4) - padding);
}

function perpendicularDistance(point, lineStart, lineEnd) {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return Math.hypot(point.x - lineStart.x, point.y - lineStart.y);
  }
  const cross = Math.abs(
    dy * point.x - dx * point.y + lineEnd.x * lineStart.y - lineEnd.y * lineStart.x,
  );
  return cross / Math.sqrt(lengthSquared);
}

// Iterative Ramer–Douglas–Peucker followed by a deterministic uniform stride
// when a hard point cap still applies. Pure: never mutates the input.
export function simplifyPenPoints(points, { tolerance = PEN_SIMPLIFY_TOLERANCE, maxPoints } = {}) {
  const source = Array.isArray(points) ? points : [];
  if (source.length <= 2) return source.slice();

  const keep = new Array(source.length).fill(false);
  keep[0] = true;
  keep[source.length - 1] = true;
  const stack = [[0, source.length - 1]];
  while (stack.length) {
    const [startIndex, endIndex] = stack.pop();
    let maxDistance = -1;
    let maxIndex = -1;
    for (let i = startIndex + 1; i < endIndex; i += 1) {
      const distance = perpendicularDistance(source[i], source[startIndex], source[endIndex]);
      if (distance > maxDistance) {
        maxDistance = distance;
        maxIndex = i;
      }
    }
    if (maxDistance > tolerance && maxIndex !== -1) {
      keep[maxIndex] = true;
      stack.push([startIndex, maxIndex], [maxIndex, endIndex]);
    }
  }

  let simplified = source.filter((_, index) => keep[index]);
  const cap = positiveInt(maxPoints);
  if (cap && simplified.length > cap) {
    // Uniform stride over the simplified sequence, always retaining both
    // endpoints — deterministic for identical inputs.
    const result = [];
    const step = (simplified.length - 1) / (cap - 1);
    for (let i = 0; i < cap; i += 1) {
      result.push(simplified[Math.round(i * step)]);
    }
    simplified = result;
  }
  return simplified;
}

// Builds the outbound stage copy: pen annotations carry simplified point
// copies bounded per stroke and in total; every other annotation and the
// editable source stage remain untouched.
export function buildOutboundStage(stage, limits) {
  const annotations = stage?.metadata?.annotations || [];
  const perStrokeCap = positiveInt(limits?.maxPenPointsPerStroke);
  const totalCap = positiveInt(limits?.maxTotalAnnotationPoints);

  let outboundAnnotations = annotations.map((annotation) => {
    if (annotation?.type !== 'pen' || !Array.isArray(annotation.localPoints)) return annotation;
    const simplified = simplifyPenPoints(annotation.localPoints, { maxPoints: perStrokeCap });
    return simplified.length === annotation.localPoints.length
      ? annotation
      : { ...annotation, localPoints: simplified };
  });

  let totalPenPoints = outboundAnnotations.reduce(
    (sum, annotation) =>
      annotation?.type === 'pen' ? sum + (annotation.localPoints?.length || 0) : sum,
    0,
  );

  if (totalCap && totalPenPoints > totalCap) {
    // Deterministic global decimation: shrink every stroke by the same ratio.
    const ratio = totalCap / totalPenPoints;
    outboundAnnotations = outboundAnnotations.map((annotation) => {
      if (annotation?.type !== 'pen' || !Array.isArray(annotation.localPoints)) return annotation;
      const target = Math.max(2, Math.floor(annotation.localPoints.length * ratio));
      return {
        ...annotation,
        localPoints: simplifyPenPoints(annotation.localPoints, { maxPoints: target }),
      };
    });
    totalPenPoints = outboundAnnotations.reduce(
      (sum, annotation) =>
        annotation?.type === 'pen' ? sum + (annotation.localPoints?.length || 0) : sum,
      0,
    );
  }

  if (outboundAnnotations === annotations) return { stage, totalPenPoints };
  return {
    stage: {
      ...stage,
      metadata: { ...stage.metadata, annotations: outboundAnnotations },
    },
    totalPenPoints,
  };
}

// The single pre-network gate: serialize the EXACT outbound SVG for the stage,
// measure real UTF-8 bytes plus decoded screenshot bytes, and report whether
// the capture fits. Callers must send nothing when `fits` is false.
export function buildOutboundCapture(stage, limits) {
  const resolved = {
    ...FEEDBACK_PAYLOAD_FALLBACK_LIMITS,
    ...(limits || {}),
  };
  const outbound = buildOutboundStage(stage, resolved);
  const annotatedSvg = buildAnnotatedSvg(outbound.stage);
  const svgBytes = utf8ByteLength(annotatedSvg);
  const screenshotBytes = dataUrlByteLength(stage?.screenshotDataUrl);
  const blockedReasons = [];
  if (svgBytes > resolved.maxSvgBytes) {
    blockedReasons.push(
      `The annotated capture is ${svgBytes.toLocaleString()} bytes; the limit is ${resolved.maxSvgBytes.toLocaleString()}.`,
    );
  }
  if (screenshotBytes > resolved.maxScreenshotBytes) {
    blockedReasons.push(
      `The screenshot is ${screenshotBytes.toLocaleString()} bytes; the limit is ${resolved.maxScreenshotBytes.toLocaleString()}.`,
    );
  }
  // Fail closed on the point budget: global decimation keeps two points per
  // stroke, so a low total cap can be unattainable — an over-cap outbound copy
  // must block instead of being reported as fitting.
  const totalCap = positiveInt(resolved.maxTotalAnnotationPoints);
  if (totalCap && outbound.totalPenPoints > totalCap) {
    blockedReasons.push(
      `The capture carries ${outbound.totalPenPoints.toLocaleString()} pen points after simplification; the limit is ${totalCap.toLocaleString()}.`,
    );
  }
  const fits = blockedReasons.length === 0;
  return {
    fits,
    // The ONE canonical outbound payload: callers must transmit this stage's
    // metadata together with `annotatedSvg` (its exact serialization) — never
    // the original editable stage the measurement did not cover.
    stage: outbound.stage,
    annotatedSvg,
    svgBytes,
    screenshotBytes,
    totalPenPoints: outbound.totalPenPoints,
    blockedReasons,
    guidance: fits ? null : FEEDBACK_PAYLOAD_BLOCK_GUIDANCE,
  };
}
